<template>
  <!-- Kode AI: the help assistant's button, bottom right beside the support
       chat's, and its panel, both in the colours of Kode's icon (the navy
       ground, the periwinkle "k", the white sparkles — Kode_AI_Logo). Ask
       how to do anything in Screenkode — typed, or spoken into the microphone
       — and it answers from what it knows about the app (the API's
       utils/kode_ai): the steps, the exact buttons, and links to the pages.
       It only explains; it never changes anything. Answers can be read aloud
       (every one, with the speaker in the header; one, with the speaker under
       it; and a spoken question is always answered aloud). "—" tucks the
       panel away; "×" closes it and keeps the conversation; the arrows start
       a new one. It fills the screen on a phone. The conversation is kept for
       the tab's session, nowhere else. -->
  <div class="kai" :class="{ 'kai--open': open, 'kai--lifted': liftForBottomNav, 'kai--beside': besideSupportChat }">
    <transition name="kai-panel">
      <section
        v-if="hasOpened"
        v-show="open"
        ref="panel"
        class="kai-panel"
        role="dialog"
        tabindex="-1"
        aria-label="Kode AI, the Screenkode help assistant"
        @keydown.esc="onEscape"
      >
        <!-- On the icon's navy: the mark, the name, and the buttons. -->
        <header class="kai-head">
          <span class="kai-head__mark" aria-hidden="true"><kode-ai-logo /></span>
          <div class="kai-head__text">
            <h2 class="kai-head__title">Kode AI</h2>
            <p class="kai-head__sub">Screenkode help assistant</p>
          </div>
          <button
            v-if="speechSupported"
            type="button"
            class="kai-icon-btn"
            :class="{ 'is-on': voiceReplies }"
            :title="voiceReplies ? 'Voice replies are on — click to turn them off' : 'Read every answer aloud'"
            :aria-label="voiceReplies ? 'Turn voice replies off' : 'Read every answer aloud'"
            :aria-pressed="voiceReplies ? 'true' : 'false'"
            @click="toggleVoiceReplies"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5z" />
              <path d="M15.5 9.25a3.5 3.5 0 0 1 0 5.5" />
              <path v-if="voiceReplies" d="M18.25 6.75a7 7 0 0 1 0 10.5" />
            </svg>
          </button>
          <button
            type="button"
            class="kai-icon-btn"
            title="New conversation"
            aria-label="Start a new conversation"
            :disabled="!messages.length || thinking"
            @click="newConversation"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4.5 12a7.5 7.5 0 0 1 13-5.15L19.5 8.75" />
              <path d="M19.5 4.5v4.25h-4.25" />
              <path d="M19.5 12a7.5 7.5 0 0 1-13 5.15L4.5 15.25" />
              <path d="M4.5 19.5v-4.25h4.25" />
            </svg>
          </button>
          <button type="button" class="kai-icon-btn" title="Minimize" aria-label="Minimize Kode AI" @click="minimize">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5.5 12h13" /></svg>
          </button>
          <button type="button" class="kai-icon-btn" title="Close" aria-label="Close Kode AI" @click="closeChat">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.75 6.75l10.5 10.5M17.25 6.75L6.75 17.25" /></svg>
          </button>
        </header>

        <div ref="scroll" class="kai-scroll" @click="onContentClick">
          <div class="kai-content" :class="{ 'kai-content--empty': !messages.length }">
            <!-- A new conversation: the mark, a hello, and questions to start with. -->
            <section v-if="!messages.length" class="kai-hero" aria-label="Getting started with Kode AI">
              <span class="kai-hero__mark" aria-hidden="true"><kode-ai-logo /></span>
              <h3 class="kai-hero__title">{{ heroTitle }}</h3>
              <p class="kai-hero__text">
                Your guide to Screenkode. Ask how to do anything in the app, like creating a donation or setting up a Minyanim roll, and I'll walk you through it step by step.
              </p>
              <div class="kai-suggestions" role="group" aria-label="Suggested questions">
                <button
                  v-for="question in suggestions"
                  :key="question"
                  type="button"
                  class="kai-chip"
                  :disabled="thinking"
                  @click="ask(question)"
                >
                  {{ question }}
                </button>
              </div>
              <p class="kai-hero__note">
                <i class="pi pi-info-circle" aria-hidden="true"></i>
                <span>I only answer questions about Screenkode, and I never change anything for you.</span>
              </p>
            </section>

            <div
              v-for="message in messages"
              :key="message.id"
              class="kai-msg"
              :class="[`kai-msg--${message.role}`, { 'is-failed': message.status === 'failed', 'is-sending': message.status === 'sending' }]"
            >
              <span v-if="message.role === 'assistant'" class="kai-avatar" aria-hidden="true"><kode-ai-logo fit="tight" /></span>
              <div class="kai-msg__body">
                <div v-if="message.role === 'assistant'" class="kai-bubble kai-bubble--assistant kai-md" v-html="renderKodeMarkdown(message.text)"></div>
                <div v-else class="kai-bubble kai-bubble--user">
                  <i v-if="message.byVoice" class="pi pi-microphone kai-bubble__voice" title="Asked by voice" aria-label="Asked by voice"></i>
                  <span class="kai-bubble__text">{{ message.text }}</span>
                </div>

                <div v-if="message.role === 'assistant'" class="kai-msg__tools">
                  <button
                    v-if="speechSupported"
                    type="button"
                    class="kai-tool"
                    :class="{ 'is-on': speakingId === message.id }"
                    :title="speakingId === message.id ? 'Stop reading' : 'Read aloud'"
                    :aria-label="speakingId === message.id ? 'Stop reading this answer' : 'Read this answer aloud'"
                    @click="toggleSpeak(message)"
                  >
                    <i class="pi" :class="speakingId === message.id ? 'pi-stop-circle' : 'pi-volume-up'" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="kai-tool"
                    :class="{ 'is-on': copiedId === message.id }"
                    :title="copiedId === message.id ? 'Copied' : 'Copy answer'"
                    aria-label="Copy this answer"
                    @click="copyAnswer(message)"
                  >
                    <i class="pi" :class="copiedId === message.id ? 'pi-check' : 'pi-copy'" aria-hidden="true"></i>
                  </button>
                </div>
                <div v-else-if="message.status === 'failed'" class="kai-msg__status" role="status">
                  <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                  <span>{{ message.error || 'Not sent.' }}</span>
                  <button type="button" class="kai-msg__status-btn" :disabled="thinking" @click="retry(message)">Try again</button>
                  <span aria-hidden="true">·</span>
                  <button type="button" class="kai-msg__status-btn" @click="discard(message)">Remove</button>
                </div>
              </div>
            </div>

            <!-- Thinking: the avatar's sparkles twinkle. -->
            <div v-if="thinking" class="kai-msg kai-msg--assistant" aria-live="polite">
              <span class="kai-avatar" aria-hidden="true"><kode-ai-logo fit="tight" twinkle /></span>
              <div class="kai-msg__body">
                <div class="kai-bubble kai-bubble--assistant kai-typing">
                  <span class="kai-typing__dots" aria-hidden="true"><i></i><i></i><i></i></span>
                  <span class="kai-typing__text">{{ thinkingLabel }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer class="kai-foot">
          <div v-if="listening" class="kai-listening" role="status">
            <span class="kai-listening__dot" aria-hidden="true"></span>
            <span class="kai-listening__text">Listening… pause when you're done to send, or tap the microphone to stop and edit.</span>
          </div>
          <!-- Where dictation is not available, the recording is sent for the
               assistant to transcribe (the support chat's own recorder). -->
          <chat-voice-recorder v-if="recording" class="kai-recorder" @send="onRecorded" @cancel="recording = false" />
          <form v-else class="kai-composer" :class="{ 'is-listening': listening }" @submit.prevent="submit">
            <textarea
              ref="input"
              v-model="draft"
              class="kai-composer__input"
              rows="1"
              :placeholder="listening ? 'Say your question…' : 'Ask how to do something in Screenkode…'"
              :maxlength="maxLength"
              aria-label="Your question"
              enterkeyhint="send"
              @keydown="onKeydown"
              @input="autogrow"
            ></textarea>
            <button
              v-if="voiceMode"
              type="button"
              class="kai-mic"
              :class="{ 'is-on': listening }"
              :title="micTitle"
              :aria-label="micTitle"
              :aria-pressed="listening ? 'true' : 'false'"
              :disabled="thinking"
              @click="toggleVoice"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="8.5" y="2.25" width="7" height="12.75" rx="3.5" />
                <path d="M5 11.5a7 7 0 0 0 14 0M12 18.5v3.25" />
              </svg>
            </button>
            <button type="submit" class="kai-send" :disabled="!canSend" title="Send" aria-label="Send your question">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21.05 3.05c-2.18-2.35-18.56 3.4-18.55 5.5.02 2.38 6.4 3.11 8.17 3.61 1.06.3 1.35.6 1.6 1.72 1.1 5.05 1.66 7.57 2.93 7.62 2.03.09 7.97-16.16 5.85-18.45Z" />
                <path d="M11.5 12.5L15 9" />
              </svg>
            </button>
          </form>
        </footer>
      </section>
    </transition>

    <!-- The button: Kode's icon, round. Open, it turns into the arrow that tucks the panel away. -->
    <button
      ref="launcher"
      type="button"
      class="kai-launcher"
      :class="{ 'is-open': open }"
      :aria-label="open ? 'Minimize Kode AI' : 'Ask Kode AI how to do something in Screenkode'"
      :aria-expanded="open ? 'true' : 'false'"
      :title="open ? 'Minimize Kode AI' : 'Ask Kode AI'"
      @click="toggle"
    >
      <span class="kai-launcher__icon kai-launcher__icon--ask" aria-hidden="true"><kode-ai-logo /></span>
      <span class="kai-launcher__icon kai-launcher__icon--close" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M6.5 9.5l5.5 5.5 5.5-5.5" /></svg>
      </span>
    </button>
  </div>
</template>
<script>
import chatVoiceRecorder from '@/components/chat/Chat_Voice_Recorder.vue';
import kodeAiLogo from '@/components/kode_ai/Kode_AI_Logo.vue';
import { askKodeAi } from '@/services/kode_ai';
import {
  createKodeDictation,
  formatSeconds,
  isDictationSupported,
  isRecordingSupported,
  isSpeechSupported,
  KODE_AI_MESSAGE_MAX_LENGTH,
  KODE_AI_OPEN_KEY,
  KODE_AI_SUGGESTIONS,
  KODE_AI_VOICE_REPLIES_KEY,
  kodeAiHistoryOf,
  kodeAiMessageId,
  kodeAiPageOf,
  kodeAiPlainText,
  readStorage,
  readStoredMessages,
  recordingToBase64,
  renderKodeMarkdown,
  speakKodeAnswer,
  stopSpeaking,
  writeStorage,
  writeStoredMessages,
} from '@/utils/kode_ai';
import { NOT_FOUND_404_ROUTE } from '@/router/routes';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';

const INPUT_MAX_HEIGHT_PX = 140;
// After this long without an answer the panel says it is still working.
const SLOW_AFTER_MS = 8000;
// Below this width the open panel fills the screen (the stylesheet's phone breakpoint).
const PHONE_MAX_WIDTH = 576;

const isTouch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

export default {
  name: 'KodeAiWidget',
  components: {
    chatVoiceRecorder,
    kodeAiLogo,
  },
  props: {
    // The support chat's button is at the corner: this one sits to its left.
    besideSupportChat: {
      type: Boolean,
      default: true,
    },
    // Sit above the phone's bottom navigation.
    liftForBottomNav: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const open = readStorage('sessionStorage', KODE_AI_OPEN_KEY) === '1';
    return {
      open,
      // The panel is only made once it is first opened.
      hasOpened: open,
      messages: readStoredMessages(),
      draft: '',
      // A question is with the assistant.
      thinking: false,
      thinkingSlow: false,
      // Dictation is on: the words land in the box as they are spoken.
      listening: false,
      // The recorder has the composer's place.
      recording: false,
      // The answer being read aloud, if any; the answer just copied.
      speakingId: null,
      copiedId: null,
      voiceReplies: readStorage('localStorage', KODE_AI_VOICE_REPLIES_KEY) === '1',
      speechSupported: isSpeechSupported(),
      // 'dictation' (Chrome, Edge, Safari: the words as they are spoken),
      // 'recording' (elsewhere: the assistant transcribes a recording), or
      // '' when the browser has no microphone API at all.
      voiceMode: isDictationSupported() ? 'dictation' : isRecordingSupported() ? 'recording' : '',
      suggestions: KODE_AI_SUGGESTIONS,
      maxLength: KODE_AI_MESSAGE_MAX_LENGTH,
      isTouch: isTouch(),
    };
  },
  computed: {
    account() {
      return this.$store.getters.account?.data || null;
    },
    firstName() {
      const name = String(this.account?.fname || '').trim();
      return name ? `${name.charAt(0).toUpperCase()}${name.slice(1)}` : '';
    },
    // "Hi Aman, I'm Kode AI", over the questions a new conversation starts with.
    heroTitle() {
      return this.firstName ? `Hi ${this.firstName}, I'm Kode AI` : "Hi, I'm Kode AI";
    },
    canSend() {
      return !this.thinking && this.draft.trim().length > 0;
    },
    micTitle() {
      if (this.listening) return 'Stop listening';
      return this.voiceMode === 'dictation' ? 'Ask by voice' : 'Record a question';
    },
    thinkingLabel() {
      return this.thinkingSlow ? 'Still working on it…' : 'Thinking…';
    },
  },
  watch: {
    open(isOpen) {
      writeStorage('sessionStorage', KODE_AI_OPEN_KEY, isOpen ? '1' : null);
      if (!isOpen) {
        this.stopVoice();
        this.stopReading();
      }
    },
    messages: {
      deep: true,
      handler(list) {
        writeStoredMessages(list);
      },
    },
  },
  methods: {
    renderKodeMarkdown,

    /* ------------------------------ opening ------------------------------ */
    toggle() {
      if (this.open) this.minimize();
      else this.openChat();
    },
    openChat() {
      this.hasOpened = true;
      this.open = true;
      this.$nextTick(() => {
        this.scrollToBottom(false);
        this.focusInput();
      });
    },
    // "—": tucked away, to open again where it was.
    minimize() {
      this.open = false;
      this.$nextTick(() => this.$refs.launcher?.focus());
    },
    // "×": closed; the conversation is kept for next time.
    closeChat() {
      this.open = false;
      this.$nextTick(() => this.$refs.launcher?.focus());
    },
    onEscape(event) {
      if (event.defaultPrevented) return;
      if (this.listening) {
        this.stopDictation();
        return;
      }
      this.minimize();
    },
    // The cursor goes in the box — not on a phone, where the keyboard would cover the answers.
    focusInput(evenOnTouch = false) {
      if (!this.open) return;
      if (this.isTouch && !evenOnTouch) {
        this.$refs.panel?.focus({ preventScroll: true });
        return;
      }
      const input = this.$refs.input;
      if (!input) {
        this.$refs.panel?.focus({ preventScroll: true });
        return;
      }
      input.focus();
      const end = input.value.length;
      try {
        input.setSelectionRange(end, end);
      } catch (error) {
        // Not a text field any more.
      }
    },
    scrollToBottom(smooth = true) {
      const scroll = this.$refs.scroll;
      if (!scroll) return;
      try {
        scroll.scrollTo({ top: scroll.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      } catch (error) {
        scroll.scrollTop = scroll.scrollHeight;
      }
    },
    // The support chat opening over this panel: this one gets out of its way.
    onDocumentClick(event) {
      if (!this.open) return;
      const target = event.target;
      if (target && typeof target.closest === 'function' && target.closest('.scw-launcher')) this.minimize();
    },
    // A link in an answer to a page of the app goes through the router; on a
    // phone the panel covers the page, so it tucks itself away.
    onContentClick(event) {
      const target = event.target;
      const link = target && typeof target.closest === 'function' ? target.closest('a[data-kai-link="internal"]') : null;
      if (!link) return;
      event.preventDefault();
      const href = link.getAttribute('href');
      if (!href) return;
      const resolved = this.$router.resolve(href);
      if (!resolved || resolved.name === NOT_FOUND_404_ROUTE) return;
      if (this.$route.fullPath !== href) this.$router.push(href).catch(() => {});
      if (window.innerWidth < PHONE_MAX_WIDTH) this.minimize();
    },

    /* ------------------------------- asking ------------------------------ */
    onKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && !this.isTouch) {
        event.preventDefault();
        this.submit();
      }
    },
    // The box grows with what is typed, up to INPUT_MAX_HEIGHT_PX, then scrolls.
    autogrow() {
      const input = this.$refs.input;
      if (!input) return;
      input.style.height = '';
      if (!input.value) {
        input.style.overflowY = 'hidden';
        return;
      }
      const borders = input.offsetHeight - input.clientHeight;
      const wanted = input.scrollHeight + borders;
      input.style.height = `${Math.min(wanted, INPUT_MAX_HEIGHT_PX)}px`;
      input.style.overflowY = wanted > INPUT_MAX_HEIGHT_PX ? 'auto' : 'hidden';
    },
    submit() {
      if (this.listening) this.stopDictation();
      if (!this.canSend) return;
      this.ask(this.draft);
    },
    /** Asks a question: the text becomes the user's message and goes to the assistant. */
    ask(text, { byVoice = false } = {}) {
      const question = String(text || '').trim();
      if (!question || this.thinking) return;
      this.draft = '';
      this.$nextTick(() => this.autogrow());
      const message = {
        id: kodeAiMessageId(),
        role: 'user',
        text: question.length > this.maxLength ? question.slice(0, this.maxLength) : question,
        status: 'sending',
        byVoice,
        createdAt: new Date().toISOString(),
      };
      this.messages.push(message);
      this.send(message);
    },
    async send(message, audio = null) {
      this.stopReading();
      this.thinking = true;
      this.thinkingSlow = false;
      window.clearTimeout(this.slowTimer);
      this.slowTimer = window.setTimeout(() => {
        this.thinkingSlow = true;
      }, SLOW_AFTER_MS);
      this.$nextTick(() => this.scrollToBottom(true));

      const history = kodeAiHistoryOf(this.messages.filter((item) => item.id !== message.id));
      const result = await askKodeAi({
        message: audio ? null : message.text,
        audio,
        history,
        page: kodeAiPageOf(this.$route),
      });
      if (this.isGone) return;

      window.clearTimeout(this.slowTimer);
      this.thinking = false;
      this.thinkingSlow = false;

      if (result.ok && result.data && result.data.answer) {
        if (audio) {
          delete this.pendingAudio[message.id];
          if (result.data.transcript) message.text = result.data.transcript;
        }
        message.status = 'done';
        message.error = null;
        const answer = {
          id: kodeAiMessageId(),
          role: 'assistant',
          text: result.data.answer,
          model: result.data.model,
          createdAt: new Date().toISOString(),
        };
        this.messages.push(answer);
        this.$nextTick(() => {
          this.scrollToBottom(true);
          if (!this.isTouch) this.focusInput();
        });
        // A spoken question is answered aloud; with voice replies on, every one is.
        if (this.voiceReplies || message.byVoice) this.readAloud(answer);
        return;
      }

      message.status = 'failed';
      message.error = result.error
        || (result.status ? 'Kode couldn\'t answer just now. Please try again.' : 'Kode couldn\'t be reached. Check your connection and try again.');
      this.$nextTick(() => this.scrollToBottom(true));
    },
    retry(message) {
      if (this.thinking) return;
      message.status = 'sending';
      message.error = null;
      this.send(message, this.pendingAudio[message.id] || null);
    },
    discard(message) {
      delete this.pendingAudio[message.id];
      this.messages = this.messages.filter((item) => item.id !== message.id);
    },
    newConversation() {
      if (this.thinking) return;
      this.stopVoice();
      this.stopReading();
      this.pendingAudio = {};
      this.messages = [];
      this.draft = '';
      this.$nextTick(() => {
        this.autogrow();
        this.scrollToBottom(false);
        this.focusInput();
      });
    },

    /* ------------------------------- voice in ---------------------------- */
    toggleVoice() {
      if (this.listening) {
        this.stopDictation();
        return;
      }
      if (this.voiceMode === 'dictation') this.startDictation();
      else if (this.voiceMode === 'recording') this.startRecording();
    },
    // Dictation: the words land in the box as they are spoken. Once the
    // speaker pauses it stops by itself and the question is sent; tapping the
    // microphone stops it and leaves the words to be edited.
    startDictation() {
      this.stopReading();
      this.dictationBase = this.draft.trim();
      this.dictationFinal = '';
      this.dictationStopRequested = false;
      this.dictation = createKodeDictation({
        onResult: this.onDictationResult,
        onEnd: this.onDictationEnd,
        onError: this.onDictationError,
      });
      if (!this.dictation) {
        this.voiceMode = isRecordingSupported() ? 'recording' : '';
        if (this.voiceMode) this.startRecording();
        return;
      }
      try {
        this.dictation.start();
        this.listening = true;
      } catch (error) {
        this.dictation = null;
        this.onDictationError('audio-capture');
      }
    },
    onDictationResult(finalText, interimText) {
      this.dictationFinal = finalText;
      this.draft = [this.dictationBase, finalText, interimText].filter(Boolean).join(' ');
      this.$nextTick(() => this.autogrow());
    },
    onDictationEnd() {
      const wasListening = this.listening;
      this.listening = false;
      this.dictation = null;
      if (!wasListening) return;
      const text = [this.dictationBase, this.dictationFinal].filter(Boolean).join(' ').trim();
      this.draft = text;
      this.$nextTick(() => this.autogrow());
      if (!this.dictationStopRequested && this.dictationFinal.trim()) {
        this.ask(text, { byVoice: true });
        return;
      }
      if (text) this.focusInput(true);
    },
    onDictationError(code) {
      // The browser ends the dictation after an error: what was heard stays in the box, unsent.
      this.dictationStopRequested = true;
      this.listening = false;
      switch (code) {
        case 'not-allowed':
        case 'service-not-allowed':
          this.$toast.error('Microphone access is blocked. Allow it for this site in your browser, then try again.', TOAST_GLOBAL_CONFIG);
          break;
        case 'no-speech':
          this.$toast.info('Kode didn\'t hear anything. Tap the microphone and ask again.', TOAST_GLOBAL_CONFIG);
          break;
        case 'audio-capture':
          this.$toast.error('No microphone was found.', TOAST_GLOBAL_CONFIG);
          break;
        case 'network':
          // Dictation runs on the browser's speech service; without it, a
          // recording the assistant transcribes does the same job.
          if (isRecordingSupported()) {
            this.voiceMode = 'recording';
            this.$toast.info('Live dictation isn\'t available right now — record your question instead.', TOAST_GLOBAL_CONFIG);
          } else {
            this.$toast.error('Voice input isn\'t available right now. Please type your question.', TOAST_GLOBAL_CONFIG);
          }
          break;
        case 'aborted':
          break;
        default:
          this.$toast.error('Voice input didn\'t work. Please type your question.', TOAST_GLOBAL_CONFIG);
      }
    },
    stopDictation() {
      this.dictationStopRequested = true;
      if (this.dictation) {
        try {
          this.dictation.stop();
        } catch (error) {
          // Already stopped.
        }
      }
      this.listening = false;
    },
    startRecording() {
      this.stopReading();
      this.recording = true;
    },
    // The recorder's send: the recording goes to the assistant, which says
    // what it heard (shown as the question) and answers it.
    async onRecorded(voice) {
      this.recording = false;
      if (!voice || !voice.blob) return;
      let audio;
      try {
        audio = await recordingToBase64(voice.blob);
      } catch (error) {
        this.$toast.error('The recording couldn\'t be read. Please try again.', TOAST_GLOBAL_CONFIG);
        return;
      }
      if (this.isGone) return;
      const message = {
        id: kodeAiMessageId(),
        role: 'user',
        text: `Voice question (${formatSeconds(voice.duration || 0)})`,
        status: 'sending',
        byVoice: true,
        createdAt: new Date().toISOString(),
      };
      this.pendingAudio[message.id] = audio;
      this.messages.push(message);
      this.send(message, audio);
    },
    stopVoice() {
      if (this.listening) this.stopDictation();
      this.recording = false;
    },

    /* ------------------------------ voice out ---------------------------- */
    readAloud(message) {
      if (!this.speechSupported || !message) return;
      this.speakingId = message.id;
      speakKodeAnswer(message.text, () => {
        if (this.speakingId === message.id) this.speakingId = null;
      });
    },
    stopReading() {
      if (!this.speechSupported) return;
      stopSpeaking();
      this.speakingId = null;
    },
    toggleSpeak(message) {
      if (this.speakingId === message.id) this.stopReading();
      else this.readAloud(message);
    },
    toggleVoiceReplies() {
      this.voiceReplies = !this.voiceReplies;
      writeStorage('localStorage', KODE_AI_VOICE_REPLIES_KEY, this.voiceReplies ? '1' : null);
      if (!this.voiceReplies) this.stopReading();
    },

    /* -------------------------------- copy ------------------------------- */
    async copyAnswer(message) {
      const text = kodeAiPlainText(message.text);
      let copied = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied) {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }
        document.body.removeChild(area);
      }
      if (!copied) {
        this.$toast.error('The answer couldn\'t be copied.', TOAST_GLOBAL_CONFIG);
        return;
      }
      this.copiedId = message.id;
      window.clearTimeout(this.copiedTimer);
      this.copiedTimer = window.setTimeout(() => {
        if (this.copiedId === message.id) this.copiedId = null;
      }, 1600);
    },
  },
  created() {
    // Plain fields: nothing in the template reads them.
    this.isGone = false;
    this.dictation = null;
    this.dictationBase = '';
    this.dictationFinal = '';
    this.dictationStopRequested = false;
    // A recorded question still waiting for its answer, by message id, so "Try again" can send it once more.
    this.pendingAudio = {};
    this.slowTimer = null;
    this.copiedTimer = null;
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    if (this.open) this.$nextTick(() => this.scrollToBottom(false));
  },
  beforeUnmount() {
    this.isGone = true;
    document.removeEventListener('click', this.onDocumentClick, true);
    window.clearTimeout(this.slowTimer);
    window.clearTimeout(this.copiedTimer);
    this.stopVoice();
    this.stopReading();
  },
};
</script>
<style scoped>
/* =========================================================================
   Kode AI, in the colours of its icon (components/kode_ai/Kode_AI_Logo):
   the icon's navy for the button and the panel's header, its periwinkle —
   deepened where white sits on it — for what the user writes and presses,
   and soft periwinkle tints for the rest. Inter, a pale page, white answers
   with a hairline border, as the support chat beside it. Sizes are the
   design's at the apps' 90% zoom (html.app-zoomed). Just above the support
   chat (980), under dialogs (1000 and up).
   ========================================================================= */
.kai {
  --kai-gap: 24px;
  --kai-lift: 0px;
  /* The icon's own colours. */
  --kai-navy: #1F222B;
  --kai-navy-raised: #2A2E3A;
  --kai-peri: #7A93CC;
  --kai-peri-light: #A5B4DF;
  --kai-peri-pale: #C9D2EF;
  /* Its periwinkle, deepened until white on it reads (5.7 : 1): the user's
     messages, Send, links, focus. */
  --kai-accent: #4865A8;
  --kai-accent-hover: #3C5795;
  --kai-accent-soft: #EEF1FA;
  --kai-accent-border: #D3DAF0;
  --kai-ring: rgba(72, 101, 168, 0.5);
  --kai-text: #1E2130;
  --kai-text-2: #474D63;
  --kai-text-3: #6B7189;
  --kai-text-4: #9AA0B5;
  --kai-icon-muted: #858BA2;
  --kai-border: #E2E5EF;
  --kai-divider: #ECEEF4;
  --kai-page: #F5F6FA;
  --kai-surface: #FFFFFF;
  --kai-danger: #DC2626;
  --kai-live: #EF4444;
  /* The support chat's recorder (components/chat/Chat_Voice_Recorder) draws
     with these, which it inherits from its conversation; here, from Kode. */
  --sc-primary: #4865A8;
  --sc-primary-hover: #3C5795;
  --sc-primary-soft: #EEF1FA;
  --sc-primary-border: #C9D2EC;
  --sc-canvas: #FFFFFF;
  --sc-soft: #F5F6FA;
  --sc-muted-bg: #EEF0F6;
  --sc-text: #1E2130;
  --sc-text-2: #474D63;
  --sc-text-3: #6B7189;
  --sc-text-4: #9AA0B5;
  --sc-text-mine: #858BA2;
  --sc-placeholder: #474D63;
  --sc-icon: #1F222B;
  --sc-border: #E2E5EF;
  --sc-hairline: #E6E9F1;
  --sc-divider: #ECEEF4;
  --sc-border-strong: #CDD3E3;
  --sc-danger: #EF4444;
  --sc-theirs: #FFFFFF;
  --sc-shadow-lg: 0 10px 28px rgba(31, 34, 43, 0.12);
  --sc-focus: 0 0 0 3px rgba(72, 101, 168, 0.18);
  --sc-font: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  position: fixed;
  right: var(--kai-gap);
  bottom: calc(var(--kai-gap) + var(--kai-lift));
  z-index: 981;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
  color: var(--kai-text);
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  pointer-events: none;
  -webkit-font-smoothing: antialiased;
}

.kai > * {
  pointer-events: auto;
}

.kai button,
.kai textarea {
  font-family: inherit;
}

/* Focus rings. The apps take the box-shadow, outline and border colour off
   every focused or pressed button (assets/style/index.css, !important), so
   each ring is drawn on the button's ::after, which that rule leaves alone. */
.kai-launcher::after,
.kai-icon-btn::after,
.kai-chip::after,
.kai-tool::after,
.kai-mic::after,
.kai-send::after,
.kai-msg__status-btn::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: inherit;
  box-shadow: 0 0 0 2px var(--kai-ring);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease;
}

.kai-launcher:focus-visible::after,
.kai-icon-btn:focus-visible::after,
.kai-chip:focus-visible::after,
.kai-tool:focus-visible::after,
.kai-mic:focus-visible::after,
.kai-send:focus-visible::after,
.kai-msg__status-btn:focus-visible::after {
  opacity: 1;
}

/* ------------------------------- the button ------------------------------ */
.kai-launcher {
  position: relative;
  /* Its own layer, so the hairline drawn under the icon sits on the navy. */
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--kai-navy);
  color: var(--kai-peri-pale);
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.15s ease, margin 0.2s ease;
}

/* Its shadow and hairline, on ::before for the same reason as the rings. */
.kai-launcher::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  box-shadow: 0 6px 16px rgba(31, 34, 43, 0.28), 0 1px 4px rgba(31, 34, 43, 0.16), inset 0 0 0 1px rgba(165, 180, 223, 0.2);
  pointer-events: none;
  transition: box-shadow 0.2s ease;
}

.kai-launcher::after {
  box-shadow: 0 0 0 3px rgba(122, 147, 204, 0.55);
}

/* Beside the support chat's button: its width and a gap to the left of it. */
.kai--beside .kai-launcher {
  margin-right: 68px;
}

.kai-launcher:hover {
  background: var(--kai-navy-raised);
  transform: translateY(-2px);
}

.kai-launcher:hover::before {
  box-shadow: 0 10px 24px rgba(31, 34, 43, 0.32), 0 2px 6px rgba(31, 34, 43, 0.16), inset 0 0 0 1px rgba(165, 180, 223, 0.32);
}

.kai-launcher:active {
  transform: scale(0.95);
}

.kai-launcher__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.kai-launcher__icon--close {
  opacity: 0;
  transform: rotate(-90deg) scale(0.6);
}

.kai-launcher__icon--close svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.kai-launcher.is-open .kai-launcher__icon--ask {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}

.kai-launcher.is-open .kai-launcher__icon--close {
  opacity: 1;
  transform: none;
}

/* Under the pointer the sparkles catch the light. */
.kai-launcher:hover :deep(.kai-logo__sparkle--big) {
  transform: rotate(90deg) scale(1.12);
}

.kai-launcher:hover :deep(.kai-logo__sparkle--small) {
  transform: scale(1.35);
}

/* ------------------------------- the panel ------------------------------- */
.kai-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 384px;
  height: min(660px, calc(100vh - 140px));
  max-width: calc(100vw - 2 * var(--kai-gap));
  overflow: hidden;
  border: 1px solid rgba(31, 34, 43, 0.14);
  border-radius: 16px;
  background: var(--kai-page);
  box-shadow: 0 18px 44px rgba(31, 34, 43, 0.18), 0 2px 8px rgba(31, 34, 43, 0.08);
  outline: none;
  transform-origin: bottom right;
}

/* The app renders at 90% on wide screens (html.app-zoomed): the panel's
   share of the screen is counted before that. */
:global(html.app-zoomed) .kai-panel {
  height: min(660px, calc((100vh - 140px) / 0.9));
}

.kai-panel-enter-active,
.kai-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.24s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.kai-panel-enter-from,
.kai-panel-leave-to {
  opacity: 0;
  transform: translateY(14px) scale(0.97);
}

/* The header, on the icon's navy. */
.kai-head {
  position: relative;
  z-index: 1;
  display: flex;
  flex: none;
  align-items: center;
  gap: 2px;
  height: 64px;
  padding: 0 10px 0 14px;
  background: var(--kai-navy);
  color: #FFFFFF;
}

/* The icon itself, a shade up from the navy around it. */
.kai-head__mark {
  display: inline-flex;
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: var(--kai-navy-raised);
  box-shadow: inset 0 0 0 1px rgba(165, 180, 223, 0.2);
}

.kai-head__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  margin-left: 11px;
}

.kai-head__title {
  margin: 0;
  overflow: hidden;
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kai-head__sub {
  margin: 0;
  overflow: hidden;
  color: var(--kai-peri-light);
  font-size: 12px;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kai-icon-btn {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--kai-peri-pale);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.kai-icon-btn svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.kai-icon-btn:hover:not(:disabled) {
  background: rgba(165, 180, 223, 0.14);
  color: #FFFFFF;
}

.kai-icon-btn::after {
  box-shadow: 0 0 0 2px rgba(165, 180, 223, 0.75);
}

.kai-icon-btn:disabled {
  color: rgba(201, 210, 239, 0.32);
  cursor: default;
}

.kai-icon-btn.is-on {
  background: rgba(122, 147, 204, 0.3);
  color: #FFFFFF;
}

/* ------------------------------ the messages ----------------------------- */
.kai-scroll {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(31, 34, 43, 0.18) transparent;
}

.kai-scroll::-webkit-scrollbar {
  width: 6px;
}

.kai-scroll::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(31, 34, 43, 0.18);
}

.kai-content {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  gap: 14px;
  padding: 16px 14px 18px;
}

/* A new conversation: the hello sits in the middle. */
.kai-content--empty {
  justify-content: center;
}

.kai-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 8px 8px;
  text-align: center;
  animation: kai-msg-in 0.2s ease-out;
}

/* The icon, as the icon: the navy square with its "k". */
.kai-hero__mark {
  display: inline-flex;
  width: 60px;
  height: 60px;
  margin-bottom: 14px;
  border-radius: 17px;
  background: var(--kai-navy);
  box-shadow: 0 10px 24px rgba(31, 34, 43, 0.2), 0 2px 6px rgba(31, 34, 43, 0.1), inset 0 0 0 1px rgba(165, 180, 223, 0.18);
}

.kai-hero__title {
  margin: 0;
  color: var(--kai-text);
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1.3;
}

.kai-hero__text {
  max-width: 300px;
  margin: 6px 0 0;
  color: var(--kai-text-2);
  font-size: 13.5px;
  line-height: 1.55;
}

.kai-hero__note {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 300px;
  margin: 16px 0 0;
  color: var(--kai-text-3);
  font-size: 12px;
  line-height: 1.45;
  text-align: left;
}

.kai-hero__note .pi {
  flex: none;
  margin-top: 2px;
  color: var(--kai-peri);
  font-size: 12px;
}

.kai-msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  animation: kai-msg-in 0.18s ease-out;
}

/* An answer starts beside Kode's mark. */
.kai-msg--assistant {
  align-items: flex-start;
}

@keyframes kai-msg-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

.kai-msg--user {
  justify-content: flex-end;
}

.kai-avatar {
  display: inline-flex;
  flex: none;
  width: 26px;
  height: 26px;
  margin-top: 2px;
  border-radius: 8px;
  background: var(--kai-navy);
  box-shadow: inset 0 0 0 1px rgba(165, 180, 223, 0.14);
}

.kai-msg__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: calc(100% - 34px);
  min-width: 0;
}

.kai-msg--user .kai-msg__body {
  align-items: flex-end;
  max-width: 86%;
}

.kai-bubble {
  position: relative;
  padding: 10px 13px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.kai-bubble--assistant {
  border: 1px solid var(--kai-border);
  border-top-left-radius: 5px;
  background: var(--kai-surface);
  color: var(--kai-text);
  box-shadow: 0 1px 2px rgba(31, 34, 43, 0.04);
}

.kai-bubble--user {
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  border-bottom-right-radius: 5px;
  background: var(--kai-accent);
  color: #FFFFFF;
  white-space: pre-wrap;
}

.kai-bubble__voice {
  flex: none;
  margin-top: 3px;
  font-size: 12px;
  opacity: 0.85;
}

.kai-msg.is-sending .kai-bubble--user {
  opacity: 0.75;
}

.kai-msg.is-failed .kai-bubble--user {
  background: #A3AAC0;
}

/* The answer's markdown (utils/kode_ai renderKodeMarkdown). */
.kai-md :deep(p) {
  margin: 0;
}

.kai-md :deep(p + p),
.kai-md :deep(p + ol),
.kai-md :deep(p + ul),
.kai-md :deep(ol + p),
.kai-md :deep(ul + p),
.kai-md :deep(ol + ul),
.kai-md :deep(ul + ol),
.kai-md :deep(ol + ol),
.kai-md :deep(ul + ul) {
  margin-top: 8px;
}

.kai-md :deep(.kai-md__heading) {
  font-weight: 600;
}

.kai-md :deep(ol),
.kai-md :deep(ul) {
  margin: 0;
  padding-left: 22px;
}

.kai-md :deep(li) {
  margin: 3px 0;
  padding-left: 2px;
}

.kai-md :deep(li::marker) {
  color: var(--kai-accent);
  font-weight: 600;
}

.kai-md :deep(strong) {
  color: var(--kai-text);
  font-weight: 600;
}

.kai-md :deep(code) {
  padding: 1px 5px;
  border-radius: 5px;
  background: var(--kai-accent-soft);
  color: #2E3A5C;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
}

.kai-md :deep(a) {
  color: var(--kai-accent);
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: rgba(72, 101, 168, 0.35);
  text-underline-offset: 2px;
}

.kai-md :deep(a:hover) {
  color: var(--kai-accent-hover);
  text-decoration-color: currentColor;
}

/* Read aloud and copy, under an answer. */
.kai-msg__tools {
  display: flex;
  gap: 2px;
  margin-left: 2px;
}

.kai-tool {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--kai-icon-muted);
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.kai-tool:hover {
  background: var(--kai-accent-soft);
  color: var(--kai-accent-hover);
}

.kai-tool.is-on {
  color: var(--kai-accent);
}

/* "Not sent. Try again · Remove" */
.kai-msg__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  color: var(--kai-danger);
  font-size: 12px;
}

.kai-msg__status-btn {
  position: relative;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: none;
  color: var(--kai-text);
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.kai-msg__status-btn:disabled {
  color: var(--kai-text-4);
  cursor: default;
}

/* Suggested questions, under the hello. */
.kai-suggestions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 7px;
  margin-top: 18px;
}

/* Its border is drawn on ::before: a focused button's own border is taken away (see the rings). */
.kai-chip {
  position: relative;
  padding: 7px 12px;
  border: 0;
  border-radius: 999px;
  background: var(--kai-surface);
  color: var(--kai-text-2);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.kai-chip::before {
  content: '';
  position: absolute;
  inset: 0;
  border: 1px solid var(--kai-accent-border);
  border-radius: inherit;
  pointer-events: none;
  transition: border-color 0.15s ease;
}

.kai-chip:hover:not(:disabled) {
  background: var(--kai-accent-soft);
  color: var(--kai-accent-hover);
}

.kai-chip:hover:not(:disabled)::before {
  border-color: var(--kai-peri);
}

.kai-chip:disabled {
  opacity: 0.6;
  cursor: default;
}

/* "Thinking…" */
.kai-typing {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--kai-text-3);
  font-size: 13px;
}

.kai-typing__dots {
  display: inline-flex;
  gap: 3px;
}

.kai-typing__dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--kai-peri);
  animation: kai-typing 1.2s ease-in-out infinite;
}

.kai-typing__dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.kai-typing__dots i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes kai-typing {
  0%, 60%, 100% { opacity: 0.35; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
}

/* ------------------------------ the composer ----------------------------- */
.kai-foot {
  flex: none;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--kai-divider);
  background: var(--kai-surface);
}

.kai-listening {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #FEF2F2;
  color: #991B1B;
  font-size: 12px;
  line-height: 1.4;
}

.kai-listening__dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--kai-live);
  animation: kai-pulse 1.1s ease-in-out infinite;
}

@keyframes kai-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.6); opacity: 0.5; }
}

.kai-recorder {
  min-height: 46px;
}

.kai-composer {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 5px 5px 5px 12px;
  border: 1px solid var(--kai-border);
  border-radius: 12px;
  background: var(--kai-surface);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.kai-composer:focus-within {
  border-color: var(--kai-peri);
  box-shadow: 0 0 0 3px rgba(122, 147, 204, 0.22);
}

.kai-composer.is-listening {
  border-color: var(--kai-live);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

/* The apps give every textarea !important borders and sizes: the box restates its own. */
.kai-composer .kai-composer__input,
.kai-composer .kai-composer__input:focus {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 36px;
  min-height: 36px;
  max-height: 140px;
  margin: 0;
  padding: 8px 0;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent;
  color: var(--kai-text);
  font-size: 14px !important;
  line-height: 20px;
  resize: none;
  box-shadow: none !important;
  outline: none;
  overflow-y: hidden;
  scrollbar-width: thin;
}

.kai-composer .kai-composer__input::placeholder {
  color: var(--kai-text-4);
  font-size: 14px;
  line-height: 20px;
}

.kai-mic,
.kai-send {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.kai-mic svg,
.kai-send svg {
  width: 19px;
  height: 19px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.kai-mic {
  background: transparent;
  color: var(--kai-text-2);
}

.kai-mic:hover:not(:disabled) {
  background: var(--kai-accent-soft);
  color: var(--kai-accent-hover);
}

.kai-mic.is-on,
.kai-mic.is-on:hover {
  background: var(--kai-live);
  color: #FFFFFF;
}

/* Listening, it glows — on ::before, as a pressed button keeps no shadow of its own. */
.kai-mic.is-on::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  animation: kai-mic-glow 1.4s ease-in-out infinite;
}

@keyframes kai-mic-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45); }
  50% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
}

.kai-mic:disabled {
  color: var(--kai-text-4);
  cursor: default;
}

.kai-send {
  background: var(--kai-accent);
  color: #FFFFFF;
}

.kai-send:hover:not(:disabled) {
  background: var(--kai-accent-hover);
}

.kai-send:disabled {
  background: #E6E9F2;
  color: #A3AAC0;
  cursor: default;
}

/* --------------------------- phones and tablets -------------------------- */
@media (max-width: 991.98px) {
  .kai--lifted {
    --kai-lift: calc(64px + env(safe-area-inset-bottom, 0px));
  }

  .kai {
    --kai-gap: 16px;
  }
}

/* Phones: the open panel fills the screen, and its own header closes it. */
@media (max-width: 575.98px) {
  .kai-launcher {
    width: 54px;
    height: 54px;
  }

  .kai--beside .kai-launcher {
    margin-right: 66px;
  }

  .kai--open {
    inset: 0;
    gap: 0;
  }

  .kai--open .kai-launcher {
    display: none;
  }

  .kai-panel {
    width: 100%;
    max-width: none;
    height: 100%;
    border: 0;
    border-radius: 0;
  }

  .kai-head {
    height: calc(58px + env(safe-area-inset-top, 0px));
    padding-top: env(safe-area-inset-top, 0px);
  }

  .kai-msg--user .kai-msg__body {
    max-width: 90%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kai-launcher,
  .kai-launcher::before,
  .kai-launcher__icon,
  .kai-chip,
  .kai-panel-enter-active,
  .kai-panel-leave-active {
    transition: none;
  }

  .kai-hero,
  .kai-msg,
  .kai-typing__dots i,
  .kai-listening__dot,
  .kai-mic.is-on::before {
    animation: none;
  }

  .kai-launcher:hover :deep(.kai-logo__sparkle--big),
  .kai-launcher:hover :deep(.kai-logo__sparkle--small) {
    transform: none;
  }
}
</style>
